import { gql, useApolloClient } from '@apollo/client'
import type { ApolloError } from '@apollo/client/errors'

import { DEFAULT_STORY } from '@/constants/DEFAULT_VALUES.ts'
import { STORY_POINTS } from '@/constants/STORY_POINTS.ts'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { STORY_SUBGROUPS } from '@/constants/STORY_SUBGROUPS.ts'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { useProjectId } from '@/hooks/useProjectId.ts'
import { getMutation } from '@/requests/apollo/mutation.ts'
import {
  INDEPENDENT_CHILDREN_GQL_SCHEMA,
  STORY_BASE_GQL_SCHEMA,
  STORY_CREATE_COMMAND_META,
} from '@/requests/apollo/schema.ts'
import { getExecuteTemplate } from '@/requests/apollo/templates.ts'
import { useQueryProjectMeta } from '@/requests/queries/useQueryProject.ts'
import { getNow } from '@/utils/date.ts'
import { getTemporaryId, isTemporaryId } from '@/utils/key.ts'
import { addCacheListElement, getStory, removeCacheListElement } from '../apollo/cache.ts'

export const useCreateStory = (storyId: string) => {
  // biome-ignore lint/style/noNonNullAssertion: This is guaranteed to be non-null
  const user = useUserContext().user!
  // biome-ignore lint/style/noNonNullAssertion:
  const projectId = useProjectId()!
  const { projectMeta } = useQueryProjectMeta()
  const { cache } = useApolloClient()

  const [execute] = getMutation('Story', STORY_CREATE_COMMAND_META)

  return {
    create: (isDuplicate = false, onError?: (error: ApolloError) => void) => {
      let requestedStory = getStory(storyId)
      if (!requestedStory) return Promise.reject(new Error(`Story with id ${storyId} not found`))

      if (isDuplicate) {
        requestedStory = {
          ...requestedStory,
          id: getTemporaryId(),
          title: `Copy of - ${requestedStory.title}`,
          blockers: [],
          comments: [],
          owners: [],
          estimate: STORY_POINTS.UNESTIMATED,
          status: STORY_STATUSES.UNSCHEDULED,
          subgroup: STORY_SUBGROUPS.DRAFT,
          acceptedAt: null,
          deletedAt: null,
          updatedAt: getNow(),
          createdAt: getNow(),
        }
      }

      const { variables, optimisticResponse } = getExecuteTemplate(
        projectMeta,
        'STORY_CREATE',
        user.id,
        { ...requestedStory, requestedBy: user },
        { ...requestedStory, requestedBy: user },
      )

      return execute({
        variables,
        optimisticResponse,
        update: (cache, { data: { executeCommand } }) => {
          for (const createdStory of executeCommand.data) {
            if (isTemporaryId(createdStory.id)) continue
            addCacheListElement(cache.identify({ __typename: 'Project', id: projectId }), createdStory, 'stories', true)
          }
        },
        onCompleted: () => {
          removeCacheListElement(cache.identify({ __typename: 'Project', id: projectId }), 'stories', '-1')
          cache.evict({ id: cache.identify(requestedStory) })
        },
        onError: (error) => {
          onError?.(error)
          if (getStory(storyId)) return
          addCacheListElement(cache.identify({ __typename: 'Project', id: projectId }), requestedStory, 'stories')
        },
      })
    },

    createCache: (storyId: string) => {
      const newStory = {
        ...DEFAULT_STORY(storyId),
        requestedBy: user,
        __typename: 'Story',
      } as WithTypename<Story>
      cache.writeFragment({
        data: newStory,
        fragment: gql`fragment NewStory on Story {
                    ${STORY_BASE_GQL_SCHEMA}
                    ${INDEPENDENT_CHILDREN_GQL_SCHEMA}
                }`,
      })

      addCacheListElement(`Project:${projectId}`, newStory, 'stories', true)
    },

    removeCache: () => {
      removeCacheListElement(cache.identify({ __typename: 'Project', id: projectId }), 'stories', storyId)
      cache.evict({ id: `Story:${storyId}` })
    },
  }
}
